import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { fetcher, queryClient } from 'merchant-api';
import { _devHooks } from 'merchant-api/vendor/optimizely';
import { analytics, merchantPortalText } from 'merchant-components';
import { router } from 'merchant-routes';
import { lazy, Suspense, useEffect } from 'react';
import { IntlProvider } from 'react-intl';
import { SWRConfig } from 'swr';
import { QueryNormalizerProvider } from '@normy/react-query';
import { AnalyticsProvider } from 'use-analytics';

const isDevMode = ['development', 'staging'].includes(import.meta.env.MODE);

// Register forced decision hook eagerly so it's available before Gondola lazy-loads.
// Gondola will overwrite this with its own implementation once it mounts.
if (isDevMode) {
  _devHooks.getForcedDecision = (experimentKey, userId) => {
    try {
      const raw = localStorage.getItem('od.dev.forced_optimizely');
      if (!raw) return undefined;
      const parsed = JSON.parse(raw) as Record<string, Record<string, string>>;
      return parsed[userId]?.[experimentKey] ?? parsed['everyone']?.[experimentKey];
    } catch {
      return undefined;
    }
  };
}

// Dynamic import so Vite's dead-code elimination drops the entire Gondola module
// graph (including optimizely.helpers.ts side effects) from the production bundle.
const Gondola = isDevMode
  ? lazy(() =>
      import('merchant-components/gondola').then((m) => ({
        default: m.Gondola,
      }))
    )
  : null;

const ReactQueryDevtools = isDevMode
  ? lazy(() => import('@tanstack/react-query-devtools').then((m) => ({ default: m.ReactQueryDevtools })))
  : null;

export const App = () => {
  useEffect(() => {analytics.page()}, [])

  return (
    <AnalyticsProvider instance={analytics}>
      <QueryNormalizerProvider queryClient={queryClient}>
        <QueryClientProvider client={queryClient}>
          <SWRConfig value={{fetcher: async (url, args) => fetcher(url, { credentials: 'include', ...args })}}>
            <IntlProvider locale="en-us" messages={merchantPortalText}>
              <RouterProvider
                router={router}
                context={{
                  queryClient,
                  applicationId: undefined,
                }}
              />
              {isDevMode && ReactQueryDevtools && <Suspense fallback={null}><ReactQueryDevtools initialIsOpen={false} /></Suspense>}
              {isDevMode && Gondola && <Suspense fallback={null}><Gondola /></Suspense>}
            </IntlProvider>
          </SWRConfig>
        </QueryClientProvider>
      </QueryNormalizerProvider>
  </AnalyticsProvider>
  )
}

export default App;
